import { useEffect, useState, useRef } from "react";
import Infoblock from "../../InfoBlock";
import { useAppSelector } from "../../../../../hooks/reduxHooks";

interface SizeQuantity {
  size: string;
  quantity: {
    total: number;
    selected: number;
  };
}

interface ProductItem {
  sku: number;
  title: string;
  image: string;
  color: string;
  qty: SizeQuantity[];
  price: number;
  addMoreAmount: number;
  addMoreSaveEach: number;
}

interface SizeItemProps {
  color: string;
  size: string;
  selectedQuantity: number;
  totalQuantity: number;
  setQty: (id: number, color: string, size: string, quantity: number) => void;
  item: any;
}

const SizeQuantityItem = ({
  color,
  size,
  selectedQuantity,
  totalQuantity,
  setQty,
  item,
}: SizeItemProps) => {
  // Set default quantity to 1 when component mounts if quantity is 0
  useEffect(() => {
    if (selectedQuantity === 0) {
      setQty(item, color, size, 1);
    }
  }, []);

  const decrementQuantity = () => {
    if (selectedQuantity > 0) {
      setQty(item, color, size, selectedQuantity - 1);
    }
  };

  const incrementQuantity = () => {
    if (selectedQuantity < totalQuantity) {
      setQty(item, color, size, selectedQuantity + 1);
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value) || 0;
    if (newValue >= 0 && newValue <= totalQuantity) {
      setQty(item, color, size, newValue);
    }
  };

  return (
    <div className="inline-flex items-center bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow transition-all duration-200 mr-2 mb-2">
      {/* <div className="text-xs font-medium text-gray-700 px-3 py-1.5 min-w-[40px] text-center">{size}</div> */}
      
      <div className="flex items-stretch h-9 border-l border-gray-200">
        <button 
          onClick={decrementQuantity}
          disabled={selectedQuantity <= 0}
          className={`w-10 flex-none flex items-center justify-center transition-colors duration-150 ${
            selectedQuantity <= 0 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:bg-gray-100 active:bg-gray-200'
          }`}
          aria-label="Decrease quantity" 
          type="button"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        </button>
        
        <div className="w-10 flex-none border-l border-r border-gray-200">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={selectedQuantity}
            onChange={handleQuantityChange}
            className="w-full h-full text-center text-sm font-medium border-0 focus:ring-0 p-0"
            aria-label={`Quantity for size ${size}`}
          />
        </div>
        
        <button 
          onClick={incrementQuantity}
          disabled={selectedQuantity >= totalQuantity}
          className={`w-10 flex-none flex items-center justify-center transition-colors duration-150 ${
            selectedQuantity >= totalQuantity 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:bg-gray-100 active:bg-gray-200'
          }`}
          aria-label="Increase quantity"
          type="button"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};

interface QuantityItemProps {
  item: any;
  product: ProductItem;
  setQty: (id: number, color: string, size: string, quantity: number) => void;
  handleDeleteProduct: (id: number) => void;
  reviewAvailable: boolean;
  setReviewAvailable: (value: boolean) => void;
}

const QuantityItem = ({
  item,
  product,
  setQty,
  handleDeleteProduct,
  reviewAvailable,
  setReviewAvailable,
}: QuantityItemProps) => {
  const { sku, title, image, color, qty } = product;
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  const MIN_REQUIRED_PER_ITEM = useAppSelector(
    (state) => state.product.product_details?.min_qty_required || 0
  );

  const calculateTotalQuantity = () =>
    qty.reduce((sum, item) => sum + item.quantity.selected, 0);

  useEffect(() => {
    const totalQty = calculateTotalQuantity();
    setTotalQuantity(totalQty);
    if (reviewAvailable) setReviewAvailable(totalQty >= MIN_REQUIRED_PER_ITEM);
  }, [qty]);

  // Check if title is truncated
  useEffect(() => {
    const checkIfTruncated = () => {
      if (titleRef.current) {
        const isTruncated = titleRef.current.scrollWidth > titleRef.current.clientWidth;
        if (isTruncated !== showTooltip) {
          setShowTooltip(isTruncated);
        }
      }
    };

    checkIfTruncated();
    window.addEventListener('resize', checkIfTruncated);
    return () => window.removeEventListener('resize', checkIfTruncated);
  }, [title]);

  return (
    <>
      {totalQuantity < MIN_REQUIRED_PER_ITEM && (
        <Infoblock type="error">
          <div className="text-sm">
            A minimum quantity of {MIN_REQUIRED_PER_ITEM} is required.
          </div>
        </Infoblock>
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4 border border-gray-100">
        <div className="flex gap-3 justify-between items-center p-3 border-b border-gray-100">
          <div className="flex gap-3 items-center flex-1 min-w-0">
            <div className="relative w-12 h-12 rounded-md overflow-hidden flex-shrink-0 border border-gray-100">
              <img 
                src={image} 
                alt={title} 
                className="w-full h-full object-cover absolute inset-0" 
              />
            </div>
            <div className="min-w-0 relative">
              <div 
                ref={titleRef}
                className="font-medium text-gray-900 text-sm truncate cursor-help max-w-[180px]"
                title={showTooltip ? title : ""}
                onMouseEnter={() => showTooltip && setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
                onTouchStart={() => showTooltip && setTooltipVisible(!tooltipVisible)}
              >
                {title}
              </div>
              {showTooltip && tooltipVisible && (
                <div className="absolute z-50 bg-gray-900 text-white text-xs rounded py-1.5 px-2.5 left-0 top-full mt-1 max-w-xs whitespace-normal shadow-lg">
                  {title}
                </div>
              )}
              <div className="text-xs text-gray-600 truncate">{color}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <div className={`text-xs ${totalQuantity === 0 ? 'text-red-500' : 'text-gray-700'} whitespace-nowrap font-medium`}>
              {totalQuantity === 0 ? (
                <span className="flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  No items
                </span>
              ) : (
                <span>
                  {totalQuantity} item{totalQuantity !== 1 && "s"}
                </span>
              )}
            </div>
            
            <div className="flex items-center">
              <button 
                onClick={() => setExpanded(!expanded)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 mr-1 touch-manipulation"
                aria-label={expanded ? "Collapse sizes" : "Expand sizes"}
              >
                <svg 
                  className={`w-5 h-5 transition-transform duration-200 ${expanded ? 'transform rotate-180' : ''}`} 
                  fill="currentColor" 
                  viewBox="0 0 20 20" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button 
                onClick={() => handleDeleteProduct(item.id)}
                className="text-gray-400 hover:text-red-500 transition-colors p-2 touch-manipulation"
                aria-label="Remove product"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {expanded && (
          <div className="p-3 bg-gray-50">
            {totalQuantity === 0 && (
              <div className="text-xs text-gray-500 mb-2 bg-white p-2 rounded border border-gray-200 flex items-center">
                <svg className="w-3 h-3 mr-1 text-amber-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Please select at least one item to continue
              </div>
            )}
            
            <div className="flex flex-wrap flex-row-reverse">
              {qty.map((item2) => (
                <SizeQuantityItem
                  key={`${color}-${item2.size}`}
                  color={color}
                  size={item2.size}
                  selectedQuantity={item2.quantity.selected}
                  totalQuantity={item2.quantity.total}
                  item={item}
                  setQty={setQty}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuantityItem;
